// Special technologies, bought with run-only spoils between waves. Permanent
// techs feed the run modifiers; the repeatable Surge tech instead pushes a timed
// production buff. All of it resets when the settlement falls.

import type { GameState, Buff } from '@core/state';
import { recomputeModifiers } from '@core/state';
import { TECH_BY_ID, techCost } from '@data/technologies';
import { bus } from '@core/events';

function addBuff(state: GameState, buff: Buff): void {
  // Same-id buff refreshes (resets its timer) rather than stacking duplicates.
  const idx = state.run.buffs.findIndex((b) => b.id === buff.id);
  if (idx >= 0) state.run.buffs[idx] = buff;
  else state.run.buffs.push(buff);
}

/** Attempts to buy one level of a technology. Returns true on success. */
export function buyTech(state: GameState, techId: string): boolean {
  const tech = TECH_BY_ID[techId];
  if (!tech) return false;
  const owned = state.run.techs[techId] ?? 0;
  if (!tech.repeatable && owned >= 1) return false;

  const cost = techCost(tech, owned);
  if (state.run.spoils < cost) return false;

  state.run.spoils -= cost;
  state.run.techs[techId] = owned + 1;

  if (tech.buff) {
    addBuff(state, {
      id: tech.id,
      label: tech.name,
      icon: tech.icon,
      prodMult: tech.buff.prodMult,
      expiresAt: state.gameTime + tech.buff.duration,
    });
  }
  if (tech.apply) recomputeModifiers(state.run);

  bus.emit('tech:bought', { techId, name: tech.name, isBuff: !!tech.buff });
  return true;
}

/** Removes expired buffs. Called each simulation tick before production. */
export function updateBuffs(state: GameState): void {
  if (state.run.buffs.length === 0) return;
  for (let i = state.run.buffs.length - 1; i >= 0; i--) {
    if (state.gameTime >= state.run.buffs[i].expiresAt) state.run.buffs.splice(i, 1);
  }
}
